import {Box, Button, TextareaAutosize, Typography, useTheme} from '@mui/material';
import React, {useCallback, useEffect, useRef} from 'react';
import {debounce} from 'lodash-es';
import {GetHandoverNotes} from '../../schema-types/GetHandoverNotes';
import {themeColors} from '../../theme/Theme';
import {useHandoverNoteStyles} from '../../features/notes/handoverNoteStyles';
import {ReviewPrintableNotes} from '../../features/notes/PrintableNotes';
import {
  FormikNote,
  HandoverNoteFields,
  getExtraText,
  getAllowedNoteLength,
  getReviewedValueFromField,
  useOnUpdateNoteMutation,
} from './HandoverNotesHelpers';
import {useAutoField} from '../../components/AutoForm/useAutoForm';
import {PreviousNotes} from '../../features/notes/PreviousNotes';
import {SaltusAny} from '../../utils/SaltusAny';
import {UPDATES} from '../../shared/UPDATES';

export const NotesEditorHandoverV1: React.FC<{
  currentNotesType: HandoverNoteFields;
  readOnly: boolean;
  _onUpdateNote: ReturnType<typeof useOnUpdateNoteMutation>['_onUpdateNote'];
  handoverData: GetHandoverNotes;
}> = ({currentNotesType, readOnly, _onUpdateNote, handoverData}) => {
  const theme = useTheme();
  const classes = useHandoverNoteStyles();
  const bodyFieldName = `${currentNotesType}.body`;
  const [parentField, parentMeta, parentHelpers] =
    useAutoField<FormikNote>(currentNotesType);
  const [bodyField, bodyMeta, bodyHelpers] =
    useAutoField<FormikNote['body']>(bodyFieldName);
  const ref = useRef<SaltusAny>(null);
  const noteLength = getAllowedNoteLength(currentNotesType);

  const defaultParentValue = {
    reviewed: false,
    id: '',
    displayName: '',
  };
  const parentValue = parentField.value || defaultParentValue;

  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
    }
  }, [currentNotesType]);

  const placeHolder = parentValue.reviewed
    ? `NO CHANGES. No note required.`
    : `Enter ${currentNotesType} notes here...${getExtraText(currentNotesType)}`;

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      _onUpdateNote({
        variables: {
          noteId: parentValue.id,
          name: parentValue.name,
          body: event.target.value,
          reviewed: getReviewedValueFromField(parentValue),
        },
      });
    },
    [parentValue.name]
  );
  const debouncedOnChange = useCallback(debounce(onChange, 350), [onChange]);

  return (
    <div
      style={{
        background: themeColors.white,
        padding: theme.spacing(2),
        marginBottom: 40,
      }}
    >
      <Box display="flex" justifyContent="space-between">
        <Typography sx={classes.currentNoteTitle}>
          Current {currentNotesType.toLowerCase()} notes:
        </Typography>
        <Box marginTop="-10px">
          <ReviewPrintableNotes handoverData={handoverData} />
        </Box>
      </Box>
      <>
        <TextareaAutosize
          ref={ref}
          autoFocus
          data-test="Note Input"
          placeholder={readOnly ? 'No notes' : placeHolder}
          style={classes.notesEditor}
          disabled={readOnly || parentValue.reviewed}
          value={bodyField.value || ''}
          id={bodyFieldName}
          onChange={(event) => {
            debouncedOnChange(event);
            bodyField.onChange(event);
          }}
          onBlur={bodyField.onBlur}
          maxLength={noteLength}
        />
        <Typography
          color={noteLength - (bodyField.value || '').length > 0 ? 'primary' : 'error'}
        >
          Character limit:{(bodyField.value || '').length}/{noteLength}
        </Typography>
        {!readOnly && parentValue.reviewed ? (
          <Button
            variant="outlined"
            onClick={() =>
              parentHelpers.setValue(UPDATES._4100, {
                ...parentValue,
                reviewed: false,
              })
            }
          >
            Add note
          </Button>
        ) : null}
        <PreviousNotes
          page={currentNotesType}
          allPreviousNotes={handoverData?.previousNotes || []}
        />
      </>
    </div>
  );
};
